import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error monitoring
 * Call this before React renders (in main.tsx)
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] No DSN configured, skipping initialization');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_BUILD_VERSION || 'unknown',

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay for debugging (only in production)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 0.1 : 0,

    // Filter PII from events
    beforeSend(event) {
      // Strip PII from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          // Remove URL query params that might contain sensitive data
          if (breadcrumb.data?.url) {
            try {
              const url = new URL(breadcrumb.data.url);
              url.search = '';
              breadcrumb.data.url = url.toString();
            } catch {
              // Keep original if URL parsing fails
            }
          }
          return breadcrumb;
        });
      }

      // Remove user IP
      if (event.user) {
        delete event.user.ip_address;
      }

      // Don't send events with sensitive data patterns
      const eventString = JSON.stringify(event);
      const sensitivePatterns = [
        /simic/i,
        /dossier/i,
        /codice_fiscale/i,
        /encryption_key/i,
      ];

      for (const pattern of sensitivePatterns) {
        if (pattern.test(eventString)) {
          // Redact the sensitive data but still send the event
          if (event.extra) {
            event.extra = { redacted: 'Event contained sensitive data patterns' };
          }
          break;
        }
      }

      return event;
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      // Network errors
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // ResizeObserver
      'ResizeObserver loop',
      // Chrome specific
      'Non-Error promise rejection captured',
    ],

    // Only send errors from our domain
    allowUrls: [
      /https?:\/\/.*\.netlify\.app/,
      /https?:\/\/localhost/,
    ],
  });
};

/**
 * Capture an exception with optional context
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>
) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Set user context for Sentry (use auth.uid only, no PII)
 */
export const setSentryUser = (userId: string | null) => {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Report web vitals to Sentry
 */
export const reportWebVitalToSentry = (metric: { name: string; value: number; id: string }) => {
  Sentry.addBreadcrumb({
    category: 'web-vital',
    message: `${metric.name}: ${Math.round(metric.value)}`,
    level: 'info',
    data: {
      metric: metric.name,
      value: metric.value,
      id: metric.id,
    },
  });
};
